/**
 * Dynamic Resolution Scaling. Watches per-frame time (or GPU-only time when
 * the caller provides it) and nudges the pixel ratio up or down to keep the
 * GPU near a target frame budget. Modern engines (Unreal, Unity) ship this
 * by default — it lets us keep a high default DPR cap on fast machines
 * while gracefully degrading on slow ones or in heavy moments (canyon
 * section, particle storms, crashes).
 *
 * Design notes:
 *  - Asymmetric: scales DOWN aggressively (drop fast when slow) and UP
 *    cautiously (let the GPU prove it's stable before reaching higher).
 *  - Hysteresis: a cooldown between adjustments + a separate sustained-
 *    streak gate prevents flicker when the signal hovers near threshold.
 *  - Bounded: scale stays between [minScale, maxScale]. The maxScale
 *    matches the device's native DPR cap; minScale is what we'll accept
 *    visually before just dropping bloom/shadows via AdaptiveQuality.
 *  - Resize cost: every change triggers backbuffer + render-target
 *    reallocation, ~1–3ms in WebGL. Cooldown is sized so this doesn't
 *    dominate when scale is bouncing around the threshold.
 *  - Refresh-rate aware: thresholds are fractions of `targetMs`. Caller
 *    detects monitor Hz from RAF intervals and calls `setTargetMs()` so
 *    the same DRS works on 60Hz, 120Hz, 144Hz, etc.
 *  - Persistence: with `storageKey`, the last stable scale is saved to
 *    localStorage so subsequent sessions skip the discovery period.
 *  - Signal-agnostic: caller passes whatever ms value drives scaling.
 *    Prefer GPU-only time (from EXT_disjoint_timer_query_webgl2) when
 *    available — it's the true GPU load and isn't masked by vsync wait.
 *    Fall back to wall-clock frame time when not.
 */
export interface DynamicResolutionOptions {
  /** Lower bound on the scale multiplier. Default 0.6. */
  minScale?: number;
  /** Upper bound (caller computes from device DPR + sanity cap). */
  maxScale: number;
  /** Starting scale. Default = persisted scale or 1.0. */
  initialScale?: number;
  /** Target frame budget in ms. Default 16.67 (60Hz). Update via setTargetMs. */
  targetMs?: number;
  /** Slow threshold as fraction of target. Default 1.10 (10% over budget). */
  slowFraction?: number;
  /**
   * Fast threshold as fraction of target. Default 1.02. With vsync, frame
   * time can't go below the target — only equal it on a hit. Setting this
   * just above 1.0 means "smoothed signal sits AT vsync, so try a tentative
   * raise." If the raise causes drops, the failed-climb memory below kicks
   * in to prevent the same climb-then-drop cycle from causing visible
   * jitter (each cycle = one missed frame = one visible racer hop).
   */
  fastFraction?: number;
  /** Step size per adjustment. Default 0.1. */
  step?: number;
  /** Frames between adjustments. Default 30 (~half second at 60Hz). */
  cooldownFrames?: number;
  /** Frames sustained over slow before dropping. Default 4 (drop fast). */
  framesBeforeDown?: number;
  /** Frames sustained under fast before raising. Default 240 (~4s at 60Hz). */
  framesBeforeUp?: number;
  /** Frames after a climb during which a drop marks that scale as a known cliff. Default 300 (~5s). */
  failedClimbWindow?: number;
  /** Frames a known-failed scale is locked out before being retried. Default 1800 (~30s). */
  failedClimbLockout?: number;
  /** localStorage key for persisting last stable scale. Optional. */
  storageKey?: string;
}

type ScaleAdjustmentDirection = 'up' | 'down';

export class DynamicResolution {
  private scale: number;
  private readonly minScale: number;
  private readonly maxScale: number;
  private readonly slowFraction: number;
  private readonly fastFraction: number;
  private readonly step: number;
  private readonly cooldownFrames: number;
  private readonly framesBeforeDown: number;
  private readonly framesBeforeUp: number;
  private readonly storageKey: string | null;
  private targetMs: number;
  private slowMs: number;
  private fastMs: number;
  private cooldown = 0;
  /** Upward resize cooldown may be interrupted by slow frames; downward cooldown may not. */
  private cooldownDirection: ScaleAdjustmentDirection | null = null;
  private slowStreak = 0;
  private fastStreak = 0;
  /** Frames the scale has held steady; used to gate persistence writes. */
  private stableStreak = 0;
  private lastPersistedScale: number;
  /**
   * Exponentially-weighted moving average of the input signal. With vsync,
   * a GPU that's just barely over budget bounces between 16.7ms (vsync hit)
   * and 33.3ms (vsync miss → wait for next refresh). Without smoothing,
   * the 16.7ms frames register as "fast" and reset the slow streak,
   * pinning DRS at a too-high scale forever. Smoothing the signal first
   * makes the bouncy 25ms average register correctly as "slow".
   */
  private smoothedMs = 0;
  /**
   * The scale we last climbed TO. If we drop within `failedClimbWindow` of
   * this commit, that scale is recorded in `failedScale` and locked out
   * for `failedClimbLockout` frames. This is the headroom mechanism: every
   * climb-drop oscillation == one missed frame == one visible racer hop,
   * so once we've discovered the cliff we should stop walking off it.
   */
  private lastClimbScale: number | null = null;
  private framesSinceClimb = Number.POSITIVE_INFINITY;
  private failedScale: number | null = null;
  private failedScaleAge = 0;
  private readonly failedClimbWindow: number;
  private readonly failedClimbLockout: number;

  constructor(opts: DynamicResolutionOptions) {
    this.maxScale = opts.maxScale;
    this.minScale = opts.minScale ?? 0.6;
    this.slowFraction = opts.slowFraction ?? 1.10;
    this.fastFraction = opts.fastFraction ?? 1.02;
    this.step = opts.step ?? 0.1;
    this.cooldownFrames = opts.cooldownFrames ?? 30;
    this.framesBeforeDown = opts.framesBeforeDown ?? 4;
    this.framesBeforeUp = opts.framesBeforeUp ?? 240;
    this.failedClimbWindow = opts.failedClimbWindow ?? 300;
    this.failedClimbLockout = opts.failedClimbLockout ?? 1800;
    this.storageKey = opts.storageKey ?? null;
    this.targetMs = opts.targetMs ?? 16.67;
    this.slowMs = this.targetMs * this.slowFraction;
    this.fastMs = this.targetMs * this.fastFraction;
    const persisted = this.readPersisted();
    const initial = opts.initialScale ?? persisted ?? 1.0;
    this.scale = Math.min(this.maxScale, Math.max(this.minScale, initial));
    this.lastPersistedScale = persisted ?? this.scale;
  }

  /** Caller passes whatever ms signal drives scaling — GPU time preferred. */
  tick(measureMs: number): number | null {
    // Exponential smoothing. Without this, vsync alternating fast/slow
    // frames cancel each other in the streak counters and DRS never drops
    // even when 50% of frames are missed.
    const alpha = 0.15;
    this.smoothedMs = this.smoothedMs === 0
      ? measureMs
      : this.smoothedMs * (1 - alpha) + measureMs * alpha;
    const signal = this.smoothedMs;

    this.framesSinceClimb++;
    if (this.failedScale !== null) {
      this.failedScaleAge++;
      if (this.failedScaleAge >= this.failedClimbLockout) {
        // Long stable period — give the cliff another shot. If the GPU
        // load has really lightened (e.g. moved to a simpler track section)
        // we want to reclaim some quality. If not, the next climb fails
        // and re-arms the lockout.
        this.failedScale = null;
        this.failedScaleAge = 0;
      }
    }

    const cooldownBlocksUp = this.cooldown > 0;
    const canPanicDropDuringCooldown = this.cooldownDirection === 'up';
    if (cooldownBlocksUp) {
      this.cooldown--;
      if (this.cooldown === 0) this.cooldownDirection = null;
      if (!canPanicDropDuringCooldown) {
        // After a downshift, let the smoothed signal decay before considering
        // another drop. After an upshift, still allow a fast panic drop below.
        this.stableStreak++;
        this.maybePersist();
        return null;
      }
    }
    this.updateStreaks(signal);

    if (this.slowStreak >= this.framesBeforeDown && this.scale > this.minScale) {
      const next = Math.max(this.minScale, this.scale - this.step);
      // If this drop happens shortly after a climb, that climb crossed the
      // cliff. Mark the climbed-to scale as known-failed so we don't try
      // to climb back to it for a long while. THIS is the headroom
      // mechanism — without it, DRS oscillates against the threshold
      // forever and every cycle costs one missed frame.
      if (this.lastClimbScale !== null && this.framesSinceClimb < this.failedClimbWindow) {
        this.failedScale = this.lastClimbScale;
        this.failedScaleAge = 0;
      }
      this.lastClimbScale = null;
      this.commit(next, 'down');
      return next;
    }
    if (cooldownBlocksUp) {
      this.stableStreak++;
      this.maybePersist();
      return null;
    }
    if (this.fastStreak >= this.framesBeforeUp && this.scale < this.maxScale) {
      // Cap the climb ceiling to one step below any known-failed scale.
      // If we're already at that ceiling, hold — don't try.
      const ceiling = this.failedScale !== null
        ? Math.max(this.minScale, this.failedScale - this.step)
        : this.maxScale;
      if (this.scale >= ceiling) {
        // Reset fastStreak so we don't keep retrying every frame.
        this.fastStreak = 0;
        this.maybePersist();
        return null;
      }
      const next = Math.min(this.maxScale, ceiling, this.scale + this.step);
      this.lastClimbScale = next;
      this.framesSinceClimb = 0;
      this.commit(next, 'up');
      return next;
    }
    this.stableStreak++;
    this.maybePersist();
    return null;
  }

  /** Update the target frame budget — call after detecting refresh rate. */
  setTargetMs(targetMs: number): void {
    if (!Number.isFinite(targetMs) || targetMs <= 0) return;
    this.targetMs = targetMs;
    this.slowMs = targetMs * this.slowFraction;
    this.fastMs = targetMs * this.fastFraction;
  }

  current(): number {
    return this.scale;
  }

  thresholds(): { slowMs: number; fastMs: number; targetMs: number } {
    return { slowMs: this.slowMs, fastMs: this.fastMs, targetMs: this.targetMs };
  }

  private updateStreaks(signal: number): void {
    if (signal > this.slowMs) {
      this.slowStreak++;
      this.fastStreak = 0;
    } else if (signal < this.fastMs) {
      this.fastStreak++;
      this.slowStreak = 0;
    } else {
      // Dead zone — hold both streaks. Only the opposite signal resets.
    }
  }

  private commit(next: number, direction: ScaleAdjustmentDirection): void {
    this.scale = next;
    this.cooldown = this.cooldownFrames;
    this.cooldownDirection = this.cooldown > 0 ? direction : null;
    this.slowStreak = 0;
    this.fastStreak = 0;
    this.stableStreak = 0;
  }

  /** Persist after the scale has held steady for ~3 seconds at 60Hz. */
  private maybePersist(): void {
    if (!this.storageKey || this.stableStreak < 180) return;
    if (this.scale === this.lastPersistedScale) return;
    try {
      localStorage.setItem(this.storageKey, JSON.stringify({ scale: this.scale }));
      this.lastPersistedScale = this.scale;
    } catch {
      // localStorage may be disabled / full; silently ignore.
    }
  }

  private readPersisted(): number | null {
    if (!this.storageKey) return null;
    try {
      const raw = localStorage.getItem(this.storageKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as { scale?: unknown };
      const scale = typeof parsed.scale === 'number' ? parsed.scale : NaN;
      if (!Number.isFinite(scale) || scale <= 0) return null;
      return scale;
    } catch {
      return null;
    }
  }
}

/**
 * Estimate display refresh interval in ms by sampling RAF deltas after the
 * page has warmed up. Use the median of N samples — robust against the
 * occasional dropped frame — and clamp the result to a plausible refresh
 * range (50–200Hz) so a noisy boot can't poison the DRS thresholds.
 *
 * Background: during the first ~1s of page life, RAF intervals are wide
 * because shaders are compiling, GLTFs are loading, and scene init is
 * burning the main thread. Sampling those would peg the probe at e.g.
 * 25ms per frame (≈40Hz "monitor"), make DRS think 25ms = vsync = lots of
 * headroom, and drive DPR UP even when the GPU is already missing real
 * frames. Skipping warmup + clamping is what prevents that pathology.
 */
export class RefreshRateProbe {
  private samples: number[] = [];
  private lastT: number | null = null;
  private framesSeen = 0;
  private done = false;
  private result: number | null = null;
  private readonly warmupFrames: number;

  constructor(
    private readonly sampleCount = 60,
    opts: { warmupFrames?: number } = {},
  ) {
    this.warmupFrames = opts.warmupFrames ?? 60;
  }

  /** Call once per frame with `performance.now()`. */
  tick(nowMs: number): number | null {
    if (this.done) return this.result;
    this.framesSeen++;
    if (this.lastT !== null && this.framesSeen > this.warmupFrames) {
      const dt = nowMs - this.lastT;
      // Reject obviously-bogus samples (tab restored, paused, page visibility).
      if (dt > 1 && dt < 100) this.samples.push(dt);
    }
    this.lastT = nowMs;
    if (this.samples.length >= this.sampleCount) {
      const sorted = [...this.samples].sort((a, b) => a - b);
      const median = sorted[Math.floor(sorted.length / 2)];
      // Clamp to plausible refresh range: 50Hz (20ms) – 200Hz (5ms). If
      // the probe lands outside that, the page was almost certainly still
      // warming up — fall back to 60Hz (the world's most common monitor).
      this.result = median >= 5 && median <= 20 ? median : 16.67;
      this.done = true;
    }
    return this.result;
  }

  isDone(): boolean {
    return this.done;
  }
}
